/**
 * Camp Hierarchy Service
 * Handles updating camp_audit_data with Program and Session data
 * Program data is long-term (preserved), Session data is year-specific (updated)
 */

import { supabase } from '@/lib/supabase';
import { getCurrentUser } from '@/lib/supabase';
import { calculateSessionExpiry, getCurrentSessionYear, shouldShowSession } from '@/utils/sessionExpiryHelpers';
import { formatCampDisplayName as formatCampDisplayNameUtil } from '@/utils/campNameFormatter';
import { fetchWithTimeout } from '@/utils/fetchWithTimeout';

/**
 * Calculate session expiry date (end of session year + 3 months)
 * @param {number} sessionYear - Year the session applies to
 * @returns {string} Expiry date in YYYY-MM-DD format
 */
function calculateExpiry(sessionYear) {
  return calculateSessionExpiry(sessionYear);
}

/**
 * Determine hierarchy level based on camp data
 * @param {Object} campData - Camp data object
 * @returns {string} 'camp', 'program', or 'session'
 */
function determineHierarchyLevel(campData) {
  if (campData.session) return 'session';
  if (campData.program) return 'program';
  return 'camp';
}

/**
 * Format camp display name with session support
 * Re-exports from utils for convenience
 * @param {Object} camp - Camp object from camp_audit_data or user_programs.program_data
 * @returns {string} Formatted camp display name (e.g., "Camp Name - Session Name")
 */
export function formatCampDisplayName(camp) {
  return formatCampDisplayNameUtil(camp);
}

/**
 * Find or create a camp_audit_data record based on name and organization
 * @param {Object} campData - Camp data with name and organization
 * @returns {Promise<Object>} Existing or newly created camp record
 */
export async function findOrCreateCampAuditRecord(campData) {
  try {
    const name = campData.name || campData.camp_name || '';
    const organization = campData.organization || campData.camp_organization || '';
    
    if (!name) {
      throw new Error('Camp name is required to find or create camp_audit_data record');
    }
    
    // Try to find existing camp by name and organization
    let query = supabase
      .from('camp_audit_data')
      .select('*')
      .eq('name', name);
    
    if (organization) {
      query = query.eq('organization', organization);
    }
    
    const { data: existingCamps, error: searchError } = await query.limit(1);
    
    if (searchError) {
      console.error('Error searching for existing camp:', searchError);
      throw searchError;
    }
    
    // If found, return existing record
    if (existingCamps && existingCamps.length > 0) {
      return existingCamps[0];
    }
    
    // Otherwise, create new record
    const newCampData = {
      audit_id: `CAMP-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`, // Generate audit_id
      name: name,
      organization: organization || null,
      source: campData.source || 'user_entry',
      location: campData.location || campData.general_location || campData.formatted_address || null,
      // Add hierarchy_level
      hierarchy_level: campData.hierarchy_level || determineHierarchyLevel(campData),
      // Add parent_place_id if provided (for sessions)
      parent_place_id: campData.parent_place_id || null,
      // Preserve program/session if provided
      program: campData.program || null,
      session: campData.session || null,
      session_year: campData.session_year || null,
      session_expires_at: campData.session ? calculateExpiry(campData.session_year || getCurrentSessionYear()) : null,
    };
    
    const { data: newCamp, error: insertError } = await supabase
      .from('camp_audit_data')
      .insert([newCampData])
      .select()
      .single();
    
    if (insertError) {
      console.error('Error creating new camp record:', insertError);
      console.error('Camp data attempted:', newCampData);
      throw new Error(`Failed to create camp record: ${insertError.message}`);
    }
    
    // Generate place_id if not already set (required for parent_place_id relationships)
    if (!newCamp.place_id) {
      const generatedPlaceId = `${name.toLowerCase().replace(/\s+/g, '-')}-${organization ? organization.toLowerCase().replace(/\s+/g, '-') : 'camp'}-${Date.now()}`;
      const { data: updatedCamp, error: updateError } = await supabase
        .from('camp_audit_data')
        .update({ place_id: generatedPlaceId })
        .eq('id', newCamp.id)
        .select()
        .single();
      
      if (updateError) {
        console.warn('Failed to set place_id:', updateError);
        // Still return the camp even if place_id update fails
      } else {
        newCamp.place_id = updatedCamp.place_id;
      }
    }
    
    return newCamp;
  } catch (error) {
    console.error('Error in findOrCreateCampAuditRecord:', error);
    throw error;
  }
}

/**
 * Get valid (non-expired) session from camp object
 * @param {Object} camp - Camp object with session and session_expires_at
 * @returns {string|null} Session name if valid, null otherwise
 */
export function getValidSession(camp) {
  if (!camp) {
    return null;
  }
  
  if (shouldShowSession(camp)) {
    return camp.session;
  }
  
  return null;
}

/**
 * Update camp hierarchy (Program and Session) in camp_audit_data
 * Program data is preserved if already exists (long-term)
 * Session data is always updated (year-specific)
 * 
 * @param {string} campId - ID of camp_audit_data record
 * @param {Object} hierarchyData - Object with program, session, and sessionYear
 * @returns {Promise<Object>} Updated camp record
 */
export async function updateCampHierarchy(campId, hierarchyData) {
  try {
    const { program, session, sessionYear } = hierarchyData;
    
    if (!campId) {
      throw new Error('Camp ID is required');
    }
    
    // First, get the existing camp record
    const { data: existingCamp, error: fetchError } = await supabase
      .from('camp_audit_data')
      .select('*')
      .eq('id', campId)
      .single();
    
    if (fetchError) {
      console.error('Error fetching existing camp:', fetchError);
      throw fetchError;
    }
    
    if (!existingCamp) {
      throw new Error(`Camp with ID ${campId} not found`);
    }
    
    // Prepare update data
    const updateData = {};
    
    // Determine and set hierarchy level
    if (session !== undefined && session !== null && session !== '') {
      updateData.hierarchy_level = 'session';
    } else if (program !== undefined && program !== null && program !== '') {
      updateData.hierarchy_level = 'program';
    } else if (existingCamp.hierarchy_level) {
      // Preserve existing hierarchy_level if no session/program is being set
    } else {
      updateData.hierarchy_level = 'camp';
    }
    
    // For Program: Only update if program is new (preserve existing program_created_at)
    if (program !== undefined && program !== null && program !== '') {
      if (!existingCamp.program_created_at) {
        // New program - set both program and program_created_at
        updateData.program = program;
        updateData.program_created_at = new Date().toISOString();
      } else {
        // Existing program - only update if different
        if (existingCamp.program !== program) {
          updateData.program = program;
          // Keep existing program_created_at (long-term data)
        }
      }
    }
    
    // For Session: Always update (sessions are year-specific)
    if (session !== undefined && session !== null && session !== '') {
      const year = sessionYear || getCurrentSessionYear();
      const expiresAt = calculateExpiry(year);
      
      updateData.session = session;
      updateData.session_created_at = new Date().toISOString();
      updateData.session_year = year;
      updateData.session_expires_at = expiresAt;
      // Ensure hierarchy_level is set to 'session'
      updateData.hierarchy_level = 'session';
      // Note: parent_place_id should be set separately via another update if needed
    } else if (session === null || session === '') {
      // Clear session data if explicitly set to null/empty
      updateData.session = null;
      updateData.session_created_at = null;
      updateData.session_year = null;
      updateData.session_expires_at = null;
    }
    
    // Update parent_place_id if provided in hierarchyData
    if (hierarchyData.parent_place_id !== undefined) {
      updateData.parent_place_id = hierarchyData.parent_place_id || null;
    }
    
    // Only update if there are changes
    if (Object.keys(updateData).length === 0) {
      return existingCamp;
    }
    
    // Update the camp record
    const { data: updatedCamp, error: updateError } = await supabase
      .from('camp_audit_data')
      .update(updateData)
      .eq('id', campId)
      .select()
      .single();
    
    if (updateError) {
      console.error('Error updating camp hierarchy:', updateError);
      throw updateError;
    }
    
    return updatedCamp;
  } catch (error) {
    console.error('Error in updateCampHierarchy:', error);
    throw error;
  }
}

/**
 * Get all sessions for a parent camp
 * @param {string} parentPlaceId - Place ID of the parent camp
 * @param {Object} options - Options for filtering
 * @param {boolean} options.includeExpired - Whether to include expired sessions
 * @returns {Promise<Array>} Array of session records from camp_audit_data
 */
export async function getCampSessions(parentPlaceId, options = {}) {
  const { includeExpired = false } = options;
  
  if (!parentPlaceId) {
    throw new Error('Parent place ID is required');
  }
  
  try {
    let query = supabase
      .from('camp_audit_data')
      .select('*')
      .eq('parent_place_id', parentPlaceId)
      .eq('hierarchy_level', 'session')
      .order('session_year', { ascending: false })
      .order('session', { ascending: true });
    
    const { data, error } = await query;
    
    if (error) {
      console.error('Error fetching sessions:', error);
      throw error;
    }
    
    if (!data) {
      return [];
    }
    
    // Filter expired sessions if requested
    if (!includeExpired) {
      const now = new Date().toISOString().split('T')[0]; // Today's date YYYY-MM-DD
      return data.filter(session => {
        if (!session.session_expires_at) return true; // Include if no expiry
        return session.session_expires_at >= now; // Only include if not expired
      });
    }
    
    return data;
  } catch (error) {
    console.error('Error in getCampSessions:', error);
    throw error;
  }
}

/**
 * Add a new session to a parent camp (OPTIMIZED with user scoping)
 * Uses user_camp_sessions table for user-specific data with fast user-scoped queries
 * @param {Object} parentCamp - Parent camp record from camp_audit_data
 * @param {Object} sessionData - Session data
 * @param {string} sessionData.session - Session name
 * @param {number} sessionData.session_year - Year
 * @param {number} sessionData.session_price - Optional price
 * @param {Object} sessionData - Other session fields
 * @param {Object} userFromContext - Logged-in user from AuthContext (avoids hanging auth calls in this flow)
 * @returns {Promise<Object>} Created session record
 */
export async function addCampSession(parentCamp, sessionData, userFromContext) {
  const startTime = Date.now();
  console.log('🔄 [OPTIMIZED] addCampSession starting...');
  console.log('🔧 addCampSession received sessionData:', {
    session: sessionData?.session,
    session_year: sessionData?.session_year,
    start_date: sessionData?.start_date,
    end_date: sessionData?.end_date,
    session_price: sessionData?.session_price
  });

  if (!parentCamp || (!parentCamp.place_id && !parentCamp.id)) {
    throw new Error('Parent camp with place_id or id is required');
  }

  if (!sessionData.session) {
    throw new Error('Session name is required');
  }

  if (!userFromContext?.id) {
    throw new Error('You must be logged in to add sessions');
  }

  try {

    const sessionYear = sessionData.session_year || getCurrentSessionYear();
    const expiresAt = calculateExpiry(sessionYear);
    const sessionName = sessionData.session.trim();

    // Add Session = create session in camp_audit_data ONLY.
    // "Add to Plan" (PublicInfoSection) writes to user_camp_sessions with camp_id = session.id.
    // Do NOT write to user_camp_sessions here to avoid duplicates and wrong camp_id (place_id).
    console.log('📤 [addCampSession] Creating session in camp_audit_data:', { sessionName, sessionYear });

    // Create session in camp_audit_data (copy parent contact info)
    const sessionRecord = {
      audit_id: `CAMP-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      name: parentCamp.name,
      organization: parentCamp.organization || null,
      program: parentCamp.program || null,
      session: sessionName,
      session_year: sessionYear,
      session_created_at: new Date().toISOString(),
      session_expires_at: expiresAt,
      hierarchy_level: 'session',
      parent_place_id: parentCamp.place_id || parentCamp.id,
      source: parentCamp.source || 'user_entry',
      location: parentCamp.location || parentCamp.general_location || parentCamp.formatted_address || null,
      user_camp_start_date: sessionData.start_date && String(sessionData.start_date).trim() ? sessionData.start_date : null,
      user_camp_end_date: sessionData.end_date && String(sessionData.end_date).trim() ? sessionData.end_date : null,
      cost: sessionData.session_price != null && sessionData.session_price !== '' ? parseFloat(sessionData.session_price) : null,
      // Copy parent contact and business info
      phone: parentCamp.phone ?? null,
      contact_phone: parentCamp.contact_phone ?? null,
      website: parentCamp.website ?? parentCamp.website_url ?? null,
      contact_email: parentCamp.contact_email ?? parentCamp.email ?? null,
      formatted_address: parentCamp.formatted_address ?? null,
      coordinates_lat: parentCamp.coordinates_lat ?? null,
      coordinates_lng: parentCamp.coordinates_lng ?? null,
      state_code: parentCamp.state_code ?? null,
      rating: parentCamp.rating ?? null,
      user_ratings_total: parentCamp.user_ratings_total ?? null,
      business_status: parentCamp.business_status ?? null,
      primary_category: parentCamp.primary_category ?? null,
      editorial_summary: parentCamp.editorial_summary ?? null,
      // Per-session age and grade range (from session data or parent)
      age_range: sessionData.age_range != null && String(sessionData.age_range).trim() !== '' ? String(sessionData.age_range).trim() : (parentCamp.age_range ?? null),
      grade_range: sessionData.grade_range != null && String(sessionData.grade_range).trim() !== '' ? String(sessionData.grade_range).trim() : (parentCamp.grade_range ?? null),
    };

    const { data: createdSession, error: insertError } = await supabase
      .from('camp_audit_data')
      .insert([sessionRecord])
      .select()
      .single();

    if (insertError) {
      console.error('❌ Error creating session in camp_audit_data:', insertError);
      throw new Error(`Failed to save session: ${insertError.message || 'Unknown error'}`);
    }

    const duration = Date.now() - startTime;
    console.log(`✅ Session saved to camp_audit_data in ${duration}ms:`, createdSession);

    return {
      id: createdSession.id,
      session: createdSession.session,
      session_year: createdSession.session_year,
      user_camp_start_date: createdSession.user_camp_start_date,
      user_camp_end_date: createdSession.user_camp_end_date,
      cost: createdSession.cost,
      parent_place_id: createdSession.parent_place_id,
      name: createdSession.name,
      organization: createdSession.organization,
      program: createdSession.program,
      location: createdSession.location || createdSession.general_location || createdSession.formatted_address,
      age_range: createdSession.age_range ?? null,
      grade_range: createdSession.grade_range ?? null,
    };
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error(`❌ [addCampSession] Error after ${duration}ms:`, error);
    console.error('❌ Error details:', {
      message: error.message,
      code: error.code,
      details: error.details,
      hint: error.hint,
    });
    console.error('❌ Parent camp at error:', parentCamp);
    console.error('❌ Session data at error:', sessionData);
    throw error;
  }
}

/**
 * Update age_range and grade_range on a camp_audit_data row (e.g. when editing a session).
 * @param {string} campId - camp_audit_data row id
 * @param {{ age_range?: string | null, grade_range?: string | null }} publicInfo
 * @returns {Promise<Object>} Updated camp record
 */
export async function updateCampAuditPublicInfo(campId, publicInfo) {
  if (!campId) {
    throw new Error('Camp ID is required');
  }
  const updateData = {};
  if (Object.prototype.hasOwnProperty.call(publicInfo, 'age_range')) {
    updateData.age_range = publicInfo.age_range == null || String(publicInfo.age_range).trim() === '' ? null : String(publicInfo.age_range).trim();
  }
  if (Object.prototype.hasOwnProperty.call(publicInfo, 'grade_range')) {
    updateData.grade_range = publicInfo.grade_range == null || String(publicInfo.grade_range).trim() === '' ? null : String(publicInfo.grade_range).trim();
  }
  if (Object.keys(updateData).length === 0) {
    const { data: existing } = await supabase.from('camp_audit_data').select('*').eq('id', campId).single();
    return existing;
  }
  const { data: updated, error } = await supabase
    .from('camp_audit_data')
    .update(updateData)
    .eq('id', campId)
    .select()
    .single();
  if (error) {
    console.error('Error updating camp_audit_data public info:', error);
    throw error;
  }
  return updated;
}

/**
 * Delete a session from camp_audit_data by id.
 * Use this for sessions created via Add Session (community sessions).
 * @param {string} sessionId - camp_audit_data row id
 * @returns {Promise<void>}
 */
export async function deleteCampSession(sessionId) {
  if (!sessionId) {
    throw new Error('Session ID is required');
  }
  const { error } = await supabase
    .from('camp_audit_data')
    .delete()
    .eq('id', sessionId);
  if (error) {
    console.error('Error deleting camp_audit_data session:', error);
    throw error;
  }
}

/**
 * Delete a session from user_camp_sessions by id.
 * Use this for user-specific sessions from the optimized user_camp_sessions table.
 * RLS policy ensures only the session owner can delete it.
 * @param {string} sessionId - user_camp_sessions row id
 * @returns {Promise<void>}
 */
export async function deleteUserCampSession(sessionId) {
  if (!sessionId) {
    throw new Error('Session ID is required');
  }
  
  // Get current user to verify authentication
  const user = await getCurrentUser();
  if (!user) {
    throw new Error('User not authenticated');
  }
  
  // Delete from user_camp_sessions
  // RLS policy ensures only the user who owns the session can delete it
  const { error } = await supabase
    .from('user_camp_sessions')
    .delete()
    .eq('id', sessionId)
    .eq('user_id', user.id); // Extra safety check (RLS also enforces this)
  
  if (error) {
    console.error('Error deleting user_camp_sessions session:', error);
    throw error;
  }
}

/**
 * Cleanup expired sessions (optional utility)
 * Marks or archives expired sessions
 * @param {Array} campIds - Optional array of camp IDs to cleanup, or null for all
 * @returns {Promise<number>} Number of camps with expired sessions
 */
export async function cleanupExpiredSessions(campIds = null) {
  try {
    let query = supabase
      .from('camp_audit_data')
      .select('id, session, session_expires_at')
      .not('session', 'is', null)
      .not('session_expires_at', 'is', null)
      .lt('session_expires_at', new Date().toISOString().split('T')[0]); // Compare dates only
    
    if (campIds && Array.isArray(campIds) && campIds.length > 0) {
      query = query.in('id', campIds);
    }
    
    const { data: expiredCamps, error } = await query;
    
    if (error) {
      console.error('Error finding expired sessions:', error);
      throw error;
    }
    
    return expiredCamps ? expiredCamps.length : 0;
  } catch (error) {
    console.error('Error in cleanupExpiredSessions:', error);
    throw error;
  }
}
